import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/redux/store';
import {
  buildAgentState,
  getSemanticPageIdentifier,
} from '@/helpers/aiChat/agentConfig';
import type { RootState } from '@/redux/store';
import type { GigawattAgentState } from '@/types/aiChat';

type AdditionalState = Record<string, unknown>;
type AgentState = GigawattAgentState & AdditionalState;

/**
 * Hook to build agent state with current user context
 *
 * This hook is responsible for ALL data extraction and computation:
 * - Extracts user data from Redux (userName, employeeId, etc.)
 * - Extracts location from React Router
 * - Extracts browser context (timezone, language)
 * - Computes derived values (currentPage from pathname)
 * - Merges with optional additional state
 *
 * Then delegates to buildAgentState() for structure definition only.
 *
 * WHERE TO ADD NEW STATE:
 * - Static user data from Redux? → Add selector here
 * - Dynamic data (updates frequently)? → Add selector here
 * - Browser/environment data? → Add extraction here
 * - Computed/derived values? → Add computation here
 *
 * @param namespaceKey - The namespace key for this chat instance (used to get lastToolset)
 * @param additionalState - Optional additional state to merge into the agent state
 * @returns Memoized agent state object with user context, location, lastToolset, and any additional state
 *
 * @example
 * // Basic usage
 * const agentState = useAgentState(CHAT_NAMESPACES.GIGAWATT_COPILOTKIT);
 *
 * @example
 * // With additional state
 * const agentState = useAgentState(
 *   CHAT_NAMESPACES.GIGAWATT_COPILOTKIT,
 *   { customField: 'value', drawerMode: true }
 * );
 */
export const useAgentState = (
  namespaceKey: string,
  additionalState?: AdditionalState
): AgentState => {
  const location = useLocation();

  // ============================================
  // DATA EXTRACTION: Get all raw data sources
  // ============================================

  // From Redux: User data
  const employeeFirstName = useAppSelector(
    (state: RootState) => state.loggedUser?.employeeFirstName
  );
  const employeeLastName = useAppSelector(
    (state: RootState) => state.loggedUser?.employeeLastName
  );
  const employeeId = useAppSelector(
    (state: RootState) => state.loggedUser?.employeeId
  );
  const userLanguage = useAppSelector(
    (state: RootState) => state.loggedUser?.userLanguage
  );

  // From Redux: Conversation state (DYNAMIC - updates frequently)
  const lastToolset = useAppSelector(
    (state: RootState) =>
      state.copilotKitChat.conversations[namespaceKey]?.lastToolset ?? null
  );

  // From Redux: Storefront context (DYNAMIC - updates whenever the shopping
  // country changes, from either the page's own selector or Gigawatt's cart tool)
  const storefrontShoppingCountry = useAppSelector(
    (state: RootState) => state.storefrontContext?.shoppingCountry
  );

  // From Browser: Environment context (STATIC - rarely changes)
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const language = useMemo(
    () => userLanguage || navigator.language,
    [userLanguage]
  );

  // ============================================
  // COMPUTED VALUES: Derive from raw data
  // ============================================

  // Compute user name from first and last name
  const userName = useMemo(
    () =>
      employeeFirstName && employeeLastName
        ? `${employeeFirstName} ${employeeLastName}`
        : undefined,
    [employeeFirstName, employeeLastName]
  );

  // Derive semantic page identifier from pathname (DYNAMIC - updates on navigation)
  const currentPage = useMemo(
    () => getSemanticPageIdentifier(location.pathname),
    [location.pathname]
  );

  // ============================================
  // STATE BUILDING: Structure the final object
  // ============================================

  // Build agent state - buildAgentState is now a pure structuring function
  const agentState = useMemo(() => {
    const baseState = buildAgentState({
      // User context (mostly static)
      userName,
      employeeId,
      timezone,
      language,
      // Location context (dynamic)
      currentPath: location.pathname,
      currentPage,
      // Storefront context (dynamic)
      storefrontShoppingCountry,
      // Conversation state (dynamic)
      lastToolset,
    });

    // Merge additional state if provided
    if (additionalState) {
      return {
        ...baseState,
        ...additionalState,
      } as AgentState;
    }

    return baseState as AgentState;
  }, [
    userName,
    employeeId,
    timezone,
    language,
    location.pathname,
    currentPage,
    storefrontShoppingCountry,
    lastToolset,
    additionalState,
  ]);

  return agentState;
};
